// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import type { PriceTable, Rates } from '../types';

/**
 * The published price list, transcribed 2026-08-09.
 *
 * It is dated in the UI and editable, because a hard-coded price table is wrong the moment the
 * list changes and a tool that quietly keeps using last year's numbers is worse than one that
 * admits it does not know. The ratios between the columns are not editable and not guessed —
 * they are the documented multipliers: a 5-minute cache write is 1.25x base input, a 1-hour write
 * is 2x, and a cache hit is 0.1x. Only the base input and output prices are entered.
 */
export const PRICES_AS_OF = '2026-08-09';
export const PRICES_SOURCE = 'https://platform.claude.com/docs/en/about-claude/pricing';

/** The documented multipliers. These are structural, not per-model. */
export const WRITE_5M_MULTIPLIER = 1.25;
export const WRITE_1H_MULTIPLIER = 2;
export const READ_MULTIPLIER = 0.1;

/** Build a full rate card from just the two numbers a price list actually varies. */
export function ratesFrom(input: number, output: number): Rates {
  return {
    input,
    write5m: input * WRITE_5M_MULTIPLIER,
    write1h: input * WRITE_1H_MULTIPLIER,
    read: input * READ_MULTIPLIER,
    output,
  };
}

export function defaultPriceTable(): PriceTable {
  return {
    asOf: PRICES_AS_OF,
    source: PRICES_SOURCE,
    families: {
      opus: { label: 'Opus 4.5 – 5', rates: ratesFrom(5, 25) },
      'opus-legacy': { label: 'Opus 4 / 4.1 (retired)', rates: ratesFrom(15, 75) },
      fable: { label: 'Fable 5 / Mythos 5', rates: ratesFrom(10, 50) },
      sonnet5: { label: 'Sonnet 5 (to 31 Aug 2026)', rates: ratesFrom(2, 10) },
      sonnet: { label: 'Sonnet 4 – 4.6', rates: ratesFrom(3, 15) },
      haiku: { label: 'Haiku 4.5', rates: ratesFrom(1, 5) },
      'haiku-legacy': { label: 'Haiku 3.5 (retired)', rates: ratesFrom(0.8, 4) },
    },
  };
}

/**
 * Fast mode, in research preview, is a flat premium on Opus 5 and Opus 4.8 rather than a
 * multiplier — $10 in / $50 out. The caching multipliers then apply on top of THAT, which is why
 * it replaces the rate card rather than scaling it.
 *
 * The session log records `usage.speed`, so this is applied from the data and never assumed.
 */
export const FAST_MODE_RATES = ratesFrom(10, 50);

/** `inference_geo: "us"` is a 1.1x multiplier on every bucket, including cache reads. */
export const US_GEO_MULTIPLIER = 1.1;

/**
 * Map a model string from the log onto a price-table family.
 *
 * Returns null for anything unrecognised — including `<synthetic>`, which Claude Code uses for
 * locally generated messages that were never sent to the API and must never be priced. An
 * unrecognised model is surfaced in the UI as unpriced rather than silently defaulted to a rate,
 * because defaulting is how a tool produces a confident wrong number.
 */
export function resolveFamily(model: string | null | undefined): string | null {
  const m = (model ?? '').toLowerCase();
  if (!m || m.startsWith('<')) return null;
  if (m.includes('fable') || m.includes('mythos')) return 'fable';
  if (m.includes('opus')) return opusFamily(m);
  if (m.includes('sonnet')) return /sonnet[-.]5(?:[^0-9]|$)/.test(m) ? 'sonnet5' : 'sonnet';
  // Only the Haiku 3.x line is retired. `undefined` means "major version 3 did not match", i.e.
  // this is 4.5 or later — the current rate card.
  if (m.includes('haiku')) return minorOf(m, 'haiku', 3) === undefined ? 'haiku' : 'haiku-legacy';
  return null;
}

/**
 * Read the minor version out of a model id, e.g. `claude-opus-4-8` -> 8.
 *
 * The awkward case, and the reason this is not a regex one-liner: a model id with no minor version
 * carries a release date in the same position. `claude-opus-4-20250514` is Opus 4, not Opus
 * 4.20250514. So a run of six digits or more is a date, and means "no minor version".
 *
 * Returns `null` when `major` is not the major version at all.
 */
function minorOf(model: string, name: string, major: number): number | null | undefined {
  const m = new RegExp(`${name}[-.]${major}(?:[-.](\\d+))?`).exec(model);
  if (!m) return undefined;
  const tail = m[1];
  if (tail === undefined || tail.length >= 6) return null; // bare major, or a release date
  return Number(tail);
}

/**
 * Opus 4 and 4.1 are the retired generation and cost THREE TIMES the current one. Everything from
 * 4.5 up is the current rate card.
 *
 * This is the single most expensive classification in the tool to get wrong, and the obvious regex
 * gets it wrong: `/opus-4(?:[-.]1)?(?:[^0-9]|$)/` matches `claude-opus-4-8`, because the character
 * after `opus-4` is a hyphen. That silently triples the reported bill of every current-generation
 * session. A unit test pins each real model id for exactly this reason.
 */
function opusFamily(model: string): string {
  const minor = minorOf(model, 'opus', 4);
  if (minor === undefined) return 'opus'; // opus-5 and anything later
  if (minor === null) return 'opus-legacy'; // bare `opus-4` plus a date
  return minor >= 5 ? 'opus' : 'opus-legacy'; // 4.5+ current, 4.1 retired
}

/** The rate card actually in force for one turn, after fast mode and data residency. */
export function ratesFor(
  table: PriceTable,
  family: string | null,
  opts: { fast?: boolean; usGeo?: boolean } = {},
): Rates | null {
  if (!family) return null;
  const base = table.families[family]?.rates;
  if (!base) return null;
  // Fast mode is documented for Opus 5 / 4.8 only. Applying it to a family that cannot use it
  // would invent a premium the user was never charged.
  const card = opts.fast && family === 'opus' ? FAST_MODE_RATES : base;
  if (!opts.usGeo) return card;
  const k = US_GEO_MULTIPLIER;
  return {
    input: card.input * k,
    write5m: card.write5m * k,
    write1h: card.write1h * k,
    read: card.read * k,
    output: card.output * k,
  };
}

/** Format USD for display. Small sums matter here, so cents are never rounded away to zero. */
export function money(usd: number): string {
  if (usd === 0) return '$0.00';
  if (Math.abs(usd) < 0.01) return `$${usd.toFixed(4)}`;
  if (Math.abs(usd) < 1) return `$${usd.toFixed(3)}`;
  return `$${usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
